import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Categoria, LayoutData, ProductoResumen } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cms } from '@/lib/cms'
import { buttonVariants } from '@/components/ui/button'
import { Seo } from '@/components/generals/seo'
import Container from '@/components/generals/container'
import PageHero from '@/components/generals/page-hero'
import CtaBanner from '@/components/generals/cta-banner'
import SectionHeading from '@/components/generals/section-heading'
import ProductCard from '@/components/catalog/product-card'
import CategoryCard from '@/components/catalog/category-card'

import {
    getCategoriaBySlug,
    getCategoriaSlugs,
    getCategorias,
    getLayoutData,
    getProductos,
    safe
} from '@/lib/catalog'

import { quoteHref, revalidateSeconds } from '@/constants/site'

interface CategoriaPageProps {
    layout: LayoutData
    categoria: Categoria
    productos: Array<ProductoResumen>
    otras: Array<Categoria>
}

const CategoriaPage: FC<CategoriaPageProps> = ({ categoria, productos, otras }): ReactNode => {
    return (
        <>
            <Seo
                title={categoria.nombre}
                description={categoria.descripcion ?? `${categoria.nombre} personalizables de TROFÉA para premiaciones y reconocimientos en Ecuador.`}
                ogImage={categoria.imagen ?? undefined}
            />

            <PageHero
                eyebrow={<span data-mdlfy-cms={cms('categorias', categoria.id, 'nombre')}>{categoria.nombre}</span>}
                title={categoria.titular
                    ? <span data-mdlfy-cms={cms('categorias', categoria.id, 'titular')}>{categoria.titular}</span>
                    : <span data-mdlfy-cms={cms('categorias', categoria.id, 'nombre')}>{categoria.nombre}</span>}
                description={categoria.introduccion ? <p data-mdlfy-cms={cms('categorias', categoria.id, 'introduccion')}>{categoria.introduccion}</p> : undefined}
                breadcrumbs={[
                    { label: 'Inicio', href: '/' },
                    { label: 'Catálogo', href: '/catalogo' },
                    { label: categoria.nombre, cms: cms('categorias', categoria.id, 'nombre') }
                ]}
                image={{ src: categoria.imagen, alt: categoria.imagen_alt }}
            >
                <div className='mt-2 flex flex-col gap-4 sm:flex-row'>
                    <Link href={`${quoteHref}?categoria=${categoria.slug}`} className={buttonVariants()}>
                        Cotizar <span data-mdlfy-cms={cms('categorias', categoria.id, 'nombre')}>{categoria.nombre}</span>
                        <ArrowRight aria-hidden='true' />
                    </Link>

                    <Link href='#productos' className={buttonVariants({ variant: 'outline' })}>
                        Ver piezas
                    </Link>
                </div>
            </PageHero>

            <section id='productos' aria-labelledby='productos-titulo' className='bg-black py-20 md:py-28'>
                <Container>
                    <div className='mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                        <SectionHeading
                            id='productos-titulo'
                            eyebrow='Referencias'
                            title={<>Piezas de <span data-mdlfy-cms={cms('categorias', categoria.id, 'nombre')}>{categoria.nombre}</span></>}
                        />

                        <p className='text-sm text-muted-foreground'>
                            {productos.length} {productos.length === 1 ? 'pieza disponible para personalizar' : 'piezas disponibles para personalizar'}
                        </p>
                    </div>

                    {productos.length > 0 ? (
                        <ul className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                            {productos.map(producto => (
                                <li key={producto.id} className='reveal'>
                                    <ProductCard producto={producto} />
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className='flex flex-col items-center gap-5 rounded-sm border border-dashed border-ivory/15 px-6 py-16 text-center'>
                            <p className='font-heading text-3xl text-foreground'>Muy pronto añadiremos piezas a esta categoría.</p>
                            <p className='max-w-md text-sm text-muted-foreground'>
                                Mientras tanto, podemos preparar una propuesta a medida para tu reconocimiento.
                            </p>
                            <Link href={`${quoteHref}?categoria=${categoria.slug}`} className={buttonVariants({ size: 'sm' })}>
                                Solicitar cotización
                            </Link>
                        </div>
                    )}

                    <p className='mt-10 text-xs leading-relaxed text-ivory/50'>
                        Imágenes, medidas y materiales de referencia. Cada pieza se define en la propuesta según cantidad y personalización.
                    </p>
                </Container>
            </section>

            {otras.length > 0 && (
                <section id='otras-categorias' aria-labelledby='otras-categorias-titulo' className='surface-grain relative bg-graphite py-20 md:py-28'>
                    <Container>
                        <SectionHeading id='otras-categorias-titulo' eyebrow='Sigue explorando' title='Otras categorías.' />

                        <div className='mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                            {otras.map(item => (
                                <CategoryCard key={item.id} categoria={item} compact />
                            ))}
                        </div>
                    </Container>
                </section>
            )}

            <CtaBanner />
        </>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await safe('slugs de categorías', getCategoriaSlugs, [])

    return {
        paths: slugs.map(slug => ({ params: { categoria: slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<CategoriaPageProps> = async ({ params }) => {
    const slug = typeof params?.categoria === 'string' ? params.categoria : ''
    const categoria = slug ? await getCategoriaBySlug(slug) : null

    if (!categoria) return { notFound: true, revalidate: revalidateSeconds }

    const [layout, productos, todas] = await Promise.all([
        getLayoutData(),
        getProductos({ categoriaId: categoria.id }),
        getCategorias()
    ])

    return {
        props: {
            layout,
            categoria,
            productos,
            otras: todas.filter(item => item.id !== categoria.id)
        },
        revalidate: revalidateSeconds
    }
}

export default CategoriaPage
